/**
 * Coalesces a fast-firing callback (a pointer drag can emit an event per
 * pixel, far more often than the display refreshes) to at most once per
 * animation frame, latest-value-wins.
 *
 * `flush` runs a pending call synchronously right now — release/commit
 * handlers call it so the last queued frame doesn't land a stale value after
 * they've already read the current one. `cancel` drops a pending call
 * without running it — teardown/unmount, or an aborted gesture that must
 * leave no trace.
 *
 * This is the primitive `useFrameThrottled` wraps for render-time callers
 * (e.g. RangeSlider's onChange). Reach for it directly — instead of the hook
 * — from an imperative pointer-event closure created outside of render (a
 * drag's window-level `pointermove` handler), where the Rules of Hooks don't
 * allow a hook call: useLineDrag and useDragReorder both do this to coalesce
 * their per-frame hit-testing.
 */
export function createFrameCoalescer<Args extends unknown[]>(
   callback: (...args: Args) => void
): { send: (...args: Args) => void; flush: () => void; cancel: () => void } {
   // Args are boxed so the snapshot below is a destructure, not a bare alias
   // (which lint bans), while still capturing before the null-out.
   let pending: { args: Args } | null = null;
   let frameHandle: number | null = null;

   const runPending = () => {
      frameHandle = null;
      if (pending) {
         const { args } = pending;
         pending = null;
         callback(...args);
      }
   };

   const cancel = () => {
      if (frameHandle !== null) {
         cancelAnimationFrame(frameHandle);
         frameHandle = null;
      }
      pending = null;
   };

   const flush = () => {
      if (frameHandle !== null) {
         cancelAnimationFrame(frameHandle);
         frameHandle = null;
      }
      runPending();
   };

   const send = (...args: Args) => {
      pending = { args };
      frameHandle ??= requestAnimationFrame(runPending);
   };

   return { send, flush, cancel };
}
